<template>
  <div class="login-screen">
    <a href="#login-main-content" class="cg-skip-link">Перейти к содержимому</a>
    <header class="login-header">
      <div class="content-read-max login-header__inner">
        <div class="login-header__left">
          <button type="button" class="login-header__back" @click="goBack">
            <Icon name="arrow-circle-left" :size="16" class="login-header__back-icon" />
            <span class="login-header__back-label">Назад</span>
          </button>
        </div>
        <img :src="brandLogoLight" alt="CardGenius" class="login-header__brand dark:hidden" />
        <img :src="brandLogoDark" alt="CardGenius" class="login-header__brand hidden dark:block" />
      </div>
    </header>

    <main v-if="!legalPage" id="login-main-content" class="login-main" tabindex="-1">
      <div class="login-content content-read-max">
        <section class="login-card" aria-label="Форма авторизации">
          <div class="login-card__header">
            <h3 class="login-card__title">{{ cardTitle }}</h3>
            <p class="login-card__subtitle">
              <template
                v-if="
                  authStep === 'resetPassword' || authStep === 'resetPasswordNew' || (authStep === 'otp' && isPasswordResetOtpFlow)
                "
              >
                Уже вспомнили пароль?
                <button type="button" class="login-card__subtitle-link" @click="leavePasswordReset">Войти</button>
              </template>
              <template v-else-if="authMode === 'register'">
                У вас уже есть профиль?
                <button type="button" class="login-card__subtitle-link" @click="setAuthMode('login')">Войти</button>
              </template>
              <template v-else>
                Нет профиля?
                <button type="button" class="login-card__subtitle-link" @click="setAuthMode('register')">Создать</button>
              </template>
            </p>
          </div>

          <div class="login-register-stack">
            <div v-if="authStep !== 'otp'" class="login-email-start">
              <template v-if="authStep === 'phone'">
                <label class="login-field">
                  <span>Укажите номер WhatsApp</span>
                  <div class="login-field__input-wrap login-field__input-wrap--phone">
                    <img :src="whatsappOriginalIcon" alt="" class="login-field__leading-icon" width="20" height="20" aria-hidden="true" />
                    <input
                      :value="phone"
                      type="tel"
                      inputmode="tel"
                      autocomplete="tel"
                      placeholder="+7 (999) 000-00-00"
                      :aria-invalid="Boolean(phoneError)"
                      :class="{ 'is-invalid': Boolean(phoneError) }"
                      @blur="phoneTouched = true"
                      @input="onPhoneInput"
                    />
                  </div>
                  <p v-if="phoneError" class="login-field__hint login-field__hint--error">{{ phoneError }}</p>
                </label>

                <button class="login-email-start__continue" :disabled="!canContinueWithPhone" @click="continueWithPhone">
                  <span class="login-email-start__continue-content">
                    <span class="login-email-start__electric" aria-hidden="true">
                      <span class="login-email-start__electric-halo"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-a"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-b"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-c"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-d"></span>
                      <Icon name="lightning" :size="16" class="login-email-start__electric-bolt" />
                    </span>
                    <span>Продолжить</span>
                  </span>
                </button>
              </template>

              <PasswordResetPhoneForm
                v-else-if="authStep === 'resetPassword'"
                v-model:phone="phone"
                :locked="isAnyLoading"
                integrated-otp-flow
                surface="login_screen"
                @code-sent="onPasswordResetCodeSent"
              />

              <template v-else-if="authStep === 'resetPasswordNew'">
                <div class="login-field">
                  <div class="login-field__label-row">
                    <label class="login-field__password-heading" for="login-recovery-password-new">Новый пароль</label>
                  </div>
                  <div class="login-field__input-wrap">
                    <input
                      id="login-recovery-password-new"
                      v-model="recoveryPassword"
                      :type="showRecoveryPassword ? 'text' : 'password'"
                      autocomplete="new-password"
                      placeholder="Минимум 6 символов"
                      :aria-invalid="Boolean(recoveryPasswordError)"
                      :class="{ 'is-invalid': Boolean(recoveryPasswordError) }"
                    />
                    <button
                      type="button"
                      class="login-field__password-toggle"
                      :aria-label="showRecoveryPassword ? 'Скрыть пароль' : 'Показать пароль'"
                      @click.stop="showRecoveryPassword = !showRecoveryPassword"
                    >
                      <Icon :name="showRecoveryPassword ? 'eye-slash' : 'eye'" :size="16" />
                    </button>
                  </div>
                  <p v-if="recoveryPasswordError" class="login-field__hint login-field__hint--error">{{ recoveryPasswordError }}</p>
                </div>
                <div class="login-field">
                  <div class="login-field__label-row">
                    <label class="login-field__password-heading" for="login-recovery-password-confirm">Повторите пароль</label>
                  </div>
                  <div class="login-field__input-wrap">
                    <input
                      id="login-recovery-password-confirm"
                      v-model="recoveryPasswordConfirm"
                      :type="showRecoveryPasswordConfirm ? 'text' : 'password'"
                      autocomplete="new-password"
                      placeholder="Ещё раз новый пароль"
                      :aria-invalid="Boolean(recoveryConfirmError)"
                      :class="{ 'is-invalid': Boolean(recoveryConfirmError) }"
                    />
                    <button
                      type="button"
                      class="login-field__password-toggle"
                      :aria-label="showRecoveryPasswordConfirm ? 'Скрыть пароль' : 'Показать пароль'"
                      @click.stop="showRecoveryPasswordConfirm = !showRecoveryPasswordConfirm"
                    >
                      <Icon :name="showRecoveryPasswordConfirm ? 'eye-slash' : 'eye'" :size="16" />
                    </button>
                  </div>
                  <p v-if="recoveryConfirmError" class="login-field__hint login-field__hint--error">{{ recoveryConfirmError }}</p>
                </div>
                <button class="login-email-start__continue" type="button" :disabled="loading" @click="submitRecoveryPassword">
                  <span class="login-email-start__continue-content">
                    <span class="login-email-start__electric" aria-hidden="true">
                      <span class="login-email-start__electric-halo"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-a"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-b"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-c"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-d"></span>
                      <Icon v-if="loading" name="loading-line" :size="16" class="is-spin login-email-start__electric-bolt" />
                      <Icon v-else name="lightning" :size="16" class="login-email-start__electric-bolt" />
                    </span>
                    <span>{{ loading ? "Сохраняем..." : "Сохранить новый пароль" }}</span>
                  </span>
                </button>
              </template>

              <template v-else>
                <label class="login-field">
                  <span>Номер WhatsApp</span>
                  <div class="login-field__input-wrap login-field__input-wrap--phone">
                    <img :src="whatsappOriginalIcon" alt="" class="login-field__leading-icon" width="20" height="20" aria-hidden="true" />
                    <input
                      :value="phone"
                      type="tel"
                      inputmode="tel"
                      autocomplete="tel"
                      placeholder="+7 (999) 000-00-00"
                      :aria-invalid="Boolean(phoneError)"
                      :class="{ 'is-invalid': Boolean(phoneError) }"
                      @blur="phoneTouched = true"
                      @input="onPhoneInput"
                    />
                  </div>
                  <p v-if="phoneError" class="login-field__hint login-field__hint--error">{{ phoneError }}</p>
                </label>

                <div class="login-field">
                  <div class="login-field__label-row">
                    <label class="login-field__password-heading" for="login-credentials-password">Пароль</label>
                    <button
                      v-if="authMode === 'login'"
                      type="button"
                      class="login-forgot-password"
                      data-testid="login-forgot-password"
                      @click="openPasswordReset"
                    >
                      Забыли пароль?
                    </button>
                  </div>
                  <div class="login-field__input-wrap">
                    <input
                      id="login-credentials-password"
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      :placeholder="authMode === 'register' ? 'Придумайте пароль' : 'Введите пароль'"
                      :autocomplete="authMode === 'register' ? 'new-password' : 'current-password'"
                      :aria-invalid="Boolean(passwordError)"
                      :class="{ 'is-invalid': Boolean(passwordError) }"
                      @blur="passwordTouched = true"
                      @input="onPasswordInput"
                    />
                    <button
                      type="button"
                      class="login-field__password-toggle"
                      :aria-label="showPassword ? 'Скрыть пароль' : 'Показать пароль'"
                      @click.stop="showPassword = !showPassword"
                    >
                      <Icon :name="showPassword ? 'eye-slash' : 'eye'" :size="16" />
                    </button>
                  </div>
                  <p v-if="passwordError" class="login-field__hint login-field__hint--error">{{ passwordError }}</p>
                </div>

                <div v-if="authMode === 'register' && !captchaBypass" class="login-step-note">
                  <p class="login-step-note__title">Подтвердите, что вы не робот</p>
                  <CloudflareTurnstile :key="captchaInstanceKey" v-model="captchaToken" :site-key="turnstileSiteKey" />
                  <p v-if="captchaError" class="login-field__hint login-field__hint--error">{{ captchaError }}</p>
                </div>

                <button class="login-email-start__continue" :disabled="!canSubmitCredentials" @click="submitCredentials">
                  <span class="login-email-start__continue-content">
                    <span class="login-email-start__electric" aria-hidden="true">
                      <span class="login-email-start__electric-halo"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-a"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-b"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-c"></span>
                      <span class="login-email-start__electric-spark login-email-start__electric-spark-d"></span>
                      <Icon v-if="loading" name="loading-line" :size="16" class="is-spin login-email-start__electric-bolt" />
                      <Icon v-else name="lightning" :size="16" class="login-email-start__electric-bolt" />
                    </span>
                    <span>{{ loading ? (authMode === "register" ? "Отправляем код..." : "Входим...") : credentialsButtonLabel }}</span>
                  </span>
                </button>
              </template>
            </div>

            <div v-else class="login-otp-card">
              <p class="login-otp-card__subtitle">
                <template v-if="isPasswordResetOtpFlow">
                  Мы отправили код в WhatsApp на
                  <strong class="login-otp-card__phone">{{ formattedPhone }}</strong>
                  для сброса пароля
                </template>
                <template v-else>
                  Мы отправили код в WhatsApp на
                  <strong class="login-otp-card__phone">{{ formattedPhone }}</strong>
                </template>
              </p>

              <div class="login-otp-inputs" @paste.prevent="onOtpPaste($event)">
                <input
                  ref="autofillInputRef"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  autocomplete="one-time-code"
                  class="login-otp-autofill-input"
                  tabindex="-1"
                  aria-hidden="true"
                  :disabled="loading"
                  @input="onOtpAutofillInput"
                  @change="onOtpAutofillInput"
                />
                <input
                  v-for="(_, index) in otpDigits"
                  :key="index"
                  :ref="(element) => setOtpInputRef(element, index)"
                  :value="otpDigits[index]"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  maxlength="1"
                  class="login-otp-input"
                  :aria-label="`Символ ${index + 1} из 4`"
                  :disabled="loading"
                  @input="onOtpInput(index, $event)"
                  @keydown="onOtpKeydown(index, $event)"
                />
              </div>

              <p v-if="otpError" class="login-field__hint login-field__hint--error login-otp-card__error">{{ otpError }}</p>
              <p v-else-if="otpCardHint" class="login-otp-card__hint">{{ otpCardHint }}</p>

              <button v-if="loading" class="login-email-start__continue login-otp-card__loading" type="button" disabled>
                <span class="login-email-start__continue-content">
                  <span class="login-email-start__electric" aria-hidden="true">
                    <Icon name="loading-line" :size="16" class="is-spin login-email-start__electric-bolt" />
                  </span>
                  <span>Проверяем код...</span>
                </span>
              </button>

              <p class="login-otp-card__resend">
                <template v-if="resendRemainingSeconds > 0"> Отправить код повторно можно через {{ resendRemainingSeconds }} сек. </template>
                <button v-else type="button" class="login-otp-card__resend-link" :disabled="loading" @click="resendOtpCode">Отправить код повторно</button>
              </p>
            </div>

            <div v-if="authStep !== 'otp' && authStep !== 'resetPassword' && authStep !== 'resetPasswordNew'" class="login-social-block">
              <p class="login-social-block__label">
                {{ authMode === "register" ? "Или зарегистрируйтесь через" : "Или войдите через" }}
              </p>
              <div class="login-social-actions" aria-label="Вход через провайдера">
                <button
                  class="login-social-actions__button login-social-actions__button--icon login-social-actions__button--google"
                  :disabled="isAnyLoading"
                  @click="loginWithProvider('google')"
                  :aria-label="authMode === 'register' ? 'Зарегистрироваться через Google' : 'Войти через Google'"
                >
                  <span class="login-social-actions__brand login-social-actions__brand--google" aria-hidden="true">
                    <svg viewBox="0 0 18 18" focusable="false">
                      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.27h2.9c1.7-1.57 2.7-3.88 2.7-6.63Z" />
                      <path
                        fill="#34A853"
                        d="M9 18c2.43 0 4.46-.8 5.95-2.17l-2.9-2.27c-.8.54-1.84.86-3.05.86-2.35 0-4.33-1.58-5.04-3.7H.96V13A9 9 0 0 0 9 18Z"
                      />
                      <path fill="#FBBC05" d="M3.96 10.72A5.42 5.42 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V5H.96A9 9 0 0 0 0 9c0 1.45.34 2.83.96 4l3-2.28Z" />
                      <path
                        fill="#EA4335"
                        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.45.9 11.43 0 9 0A9 9 0 0 0 .96 5l3 2.28c.7-2.13 2.69-3.7 5.04-3.7Z"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>

          <p
            v-if="authMode === 'register' && authStep !== 'otp' && authStep !== 'resetPassword' && authStep !== 'resetPasswordNew'"
            class="login-legal-consent"
          >
            Регистрируясь, вы принимаете
            <button type="button" class="login-legal-consent__link" @click="openLegal('offer')">публичную оферту</button>,
            <br />
            <button type="button" class="login-legal-consent__link" @click="openLegal('terms')">условия использования</button>
            и
            <button type="button" class="login-legal-consent__link" @click="openLegal('privacy')">политику конфиденциальности</button>.
          </p>
          <p v-if="error" class="error">{{ error }}</p>
        </section>
      </div>
    </main>

    <main v-else id="login-main-content" class="login-main" tabindex="-1">
      <div class="login-content content-read-max">
        <section class="login-legal-head">
          <h2 class="page-title">{{ legalTitle }}</h2>
          <p class="login-legal-head__subtitle">{{ legalSubtitle }}</p>
        </section>

        <section class="login-legal-card">
          <template v-if="legalPage === 'offer'">
            <p>
              Настоящая оферта является публичным предложением заключить пользовательское соглашение об условиях использования платформы CardGenius (далее —
              Соглашение) с любым заинтересованным физическим или юридическим лицом (далее — Пользователь).
            </p>
            <h3>1. Акцепт оферты</h3>
            <p>
              Акцептом оферты признается регистрация на платформе, авторизация в личном кабинете или оплата выбранного тарифного плана. Акцепт означает полное и
              безоговорочное согласие с условиями настоящей оферты, Условиями использования и Политикой конфиденциальности.
            </p>
            <h3>2. Предмет соглашения</h3>
            <p>
              Компания предоставляет Пользователю доступ к функционалу платформы CardGenius для быстрого создания и улучшения карточек товаров, включая
              генерацию и редактирование контента, хранение версий и работу с рекомендациями.
            </p>
            <h3>3. Условные единицы «geni» и баланс</h3>
            <ul>
              <li>
                «Geni» — внутрисервисные условные единицы учёта, отражающие объём предоплаченных возможностей использования функций платформы. Условные единицы
                не являются электронными денежными средствами, цифровой валютой, средством платежа и не подлежат обмену на деньги, кроме случаев возврата в
                соответствии с законодательством и настоящей офертой.
              </li>
              <li>
                Зачисление geni на баланс Пользователя производится после подтверждения оплаты выбранного пакета (или иного платного предложения) в интерфейсе
                сервиса. Списание geni происходит при выполнении действий, для которых в интерфейсе указана стоимость в geni (например, генерация или улучшение
                контента).
              </li>
              <li>
                Состав пакетов, количество geni, цены и расчёт эквивалентов использования отображаются в интерфейсе на дату оформления заказа и являются
                существенными условиями.
              </li>
              <li>
                Исполнитель вправе изменять тарифы и «стоимость» операций в geni для новых приобретений, уведомляя об этом на сайте или в интерфейсе. Ранее
                приобретённые и зачисленные geni сохраняются на балансе в объёме зачисления, если иное прямо не предусмотрено отдельным уведомлением при наличии
                законных оснований.
              </li>
            </ul>
            <h3>4. Порядок использования</h3>
            <ul>
              <li>Пользователь самостоятельно проходит регистрацию и предоставляет достоверные данные.</li>
              <li>Пользователь обязуется сохранять конфиденциальность учетных данных.</li>
              <li>Для полной автоматизации допускается подключение API маркетплейсов при наличии прав и разрешений.</li>
              <li>Пользователь несет ответственность за законность загружаемого контента и права на него.</li>
            </ul>
            <h3>5. Стоимость, расчёты и дистанционная продажа</h3>
            <p>
              Стоимость тарифов и пакетов geni, перечень функций указываются в интерфейсе платформы. Цены доводятся до сведения Пользователя до момента оплаты.
              Оплата осуществляется в валюте, указанной в интерфейсе и/или платёжном виджете, на условиях предварительной оплаты, если иное явно не указано в
              карточке предложения.
            </p>
            <p>
              Моментом заключения договора возмездного оказания услуг в части платных функций считается акцепт оферты и совершение Пользователем оплаты либо
              иное действие, прямо предусмотренное интерфейсом как подтверждение заказа.
            </p>
            <p>
              Если Пользователь является потребителем по смыслу Закона Республики Казахстан «О защите прав потребителей», к отношениям сторон применяются
              императивные нормы указанного Закона и Гражданского кодекса Республики Казахстан в части, не урегулированной настоящей офертой.
            </p>
            <h3>6. Ограничение ответственности</h3>
            <p>
              Компания предоставляет платформу и инструменты в состоянии «как есть» в пределах действующего законодательства. Рекомендации и контент платформы
              не являются гарантией достижения конкретных коммерческих показателей, так как результат зависит от рыночных факторов, конкуренции, категории и
              действий Пользователя.
            </p>
            <h3>7. Возвраты денежных средств, отказ от услуг и блокировка</h3>
            <p>
              Потребитель вправе отказаться от договора возмездного оказания услуг и потребовать возврата уплаченных денежных средств в случаях и порядке,
              предусмотренных законодательством Республики Казахстан, с учётом фактически оказанных услуг и понесённых Исполнителем расходов.
            </p>
            <p>
              При возврате по основаниям законодательства Республики Казахстан неиспользованные на дату обращения geni подлежат перерасчёту в денежный
              эквивалент пропорционально цене приобретённого пакета (за вычетом фактически израсходованных единиц по тарифу пакета), если иной порядок не
              установлен императивными нормами и не согласован дополнительно.
            </p>
            <p>
              При нарушении условий оферты Компания вправе ограничить доступ к сервису до устранения нарушений. Незачисление geni при техническом сбое
              устраняется по обращению Пользователя при подтверждении факта оплаты.
            </p>
            <h3>8. Применимое право и споры</h3>
            <p>
              К отношениям Сторон применяется право Республики Казахстан, без ущерба для императивных норм права, подлежащих применению в конкретном случае.
            </p>
            <p>
              Споры подлежат разрешению путём переговоров. При недостижении согласия спор подлежит рассмотрению в суде по правилам подсудности, установленным
              законодательством Республики Казахстан, либо в ином компетентном суде согласно императивным нормам применимого права.
            </p>
            <h3>9. Реквизиты исполнителя</h3>
            <p>
              ТОО «Окто Тех»<br />
              Республика Казахстан, г. Алматы, Бостандыкский район, ул. Маркова, 22/37, офис 7<br />
              БИН: 241240019564<br />
              Email: directors@okto-mail.kz
            </p>
            <p>
              Текст учитывает положения Гражданского кодекса Республики Казахстан, Закона Республики Казахстан «О защите прав потребителей» и Закона Республики
              Казахстан от 21.05.2013 № 94-V «О персональных данных и их защите». Для окончательного утверждения рекомендуется проверка профильным юристом с
              учётом актуальной организационно-правовой формы и платёжных процессов Исполнителя.
            </p>
          </template>

          <template v-else-if="legalPage === 'terms'">
            <p>
              Настоящие Условия регулируют порядок использования сервиса CardGenius. Регистрируясь в сервисе или продолжая использование, вы подтверждаете
              согласие с положениями данного документа, а также с
              <button type="button" class="login-legal-inline-link" @click="openLegal('privacy')">Политикой конфиденциальности</button>
              и
              <button type="button" class="login-legal-inline-link" @click="openLegal('offer')">Публичной офертой</button>.
            </p>
            <h3>1. Назначение сервиса</h3>
            <p>
              CardGenius предоставляет инструменты для подготовки контента карточек товаров Wildberries: структуры описаний, вариантов заголовков, рекомендаций
              по улучшению и сценариев автоматизации. Сервис предназначен для коммерческого использования продавцами и командами, работающими с маркетплейсом
              WB.
            </p>
            <h3>2. Доступ и аккаунт</h3>
            <ul>
              <li>Пользователь обязан указывать достоверные данные при регистрации.</li>
              <li>Пользователь несет ответственность за сохранность доступа к аккаунту.</li>
              <li>Передача доступа третьим лицам без согласования с владельцем аккаунта запрещена.</li>
            </ul>
            <h3>3. Баланс geni и платные функции</h3>
            <p>
              Для доступа к отдельным функциям сервиса может требоваться положительный баланс условных единиц «geni». Geni не являются денежными средствами;
              порядок зачисления, списания и отображения баланса определяется интерфейсом CardGenius и
              <button type="button" class="login-legal-inline-link" @click="openLegal('offer')">Публичной офертой</button>.
            </p>
            <ul>
              <li>
                Пользователь самостоятельно выбирает пакет пополнения и подтверждает заказ; зачисление geni производится после подтверждения оплаты платёжным
                провайдером.
              </li>
              <li>
                Списание geni выполняется при запуске платных операций (стоимость в geni указывается до подтверждения действия, если такой сценарий предусмотрен
                интерфейсом).
              </li>
              <li>
                Запрещено использовать баланс и платежи способами, нарушающими законодательство, правила платёжных систем или настоящие Условия (в том числе
                обход лимитов, мошенничество, злоупотребление бонусами).
              </li>
              <li>
                Возврат денежных средств и порядок отказа от услуг для потребителей — в соответствии с
                <button type="button" class="login-legal-inline-link" @click="openLegal('offer')">офертой</button>
                и императивными нормами законодательства Республики Казахстан.
              </li>
            </ul>
            <h3>4. Подключение WB API и данные</h3>
            <p>
              Для полной автоматизации пользователь может подключить WB API. Пользователь подтверждает наличие прав на использование соответствующих ключей и
              разрешений. CardGenius использует переданные данные для выполнения функций сервиса и не использует их вне цели предоставления услуг.
            </p>
            <h3>5. Ограничение ответственности</h3>
            <p>
              Рекомендации сервиса не являются гарантией конкретного уровня продаж, CTR или конверсии. На результат также влияют категория товара, цена,
              конкуренция, логистика и иные внешние факторы маркетплейса.
            </p>
            <h3>6. Интеллектуальная собственность</h3>
            <p>
              Права на платформу, интерфейсы и служебные материалы принадлежат правообладателю сервиса. Пользователь сохраняет права на собственный загруженный
              контент и подтверждает законность его использования.
            </p>
            <h3>7. Изменения условий</h3>
            <p>
              Мы вправе обновлять настоящие Условия. Актуальная редакция публикуется на сайте и применяется с момента публикации, если иное не предусмотрено
              новой редакцией.
            </p>
          </template>

          <template v-else>
            <p>
              Настоящая Политика объясняет, какие данные мы обрабатываем при использовании CardGenius, для чего мы это делаем и как пользователь может управлять
              своими данными. Использование сервиса означает согласие с положениями данной Политики и
              <button type="button" class="login-legal-inline-link" @click="openLegal('terms')">Условиями использования</button>.
            </p>
            <h3>1. Общие положения</h3>
            <p>
              Настоящая Политика разработана в соответствии с Законом Республики Казахстан от 21.05.2013 № 94-V «О персональных данных и их защите» (далее —
              Закон о персональных данных РК) и иными нормами законодательства Республики Казахстан, подлежащими применению к обработке персональных данных
              пользователей, являющихся субъектами персональных данных.
            </p>
            <p>
              Оператор персональных данных — лицо, указанное в разделе реквизитов
              <button type="button" class="login-legal-inline-link" @click="openLegal('offer')">Публичной оферты</button>
              (далее — Оператор). Использование сервиса означает согласие с настоящей Политикой; отдельные виды обработки (например, рассылка) могут требовать
              дополнительного согласия, если это предусмотрено законом.
            </p>
            <h3>2. Состав обрабатываемых персональных данных</h3>
            <ul>
              <li>Идентификационные и контактные данные: адрес электронной почты, иные сведения, добровольно указанные при регистрации или в профиле.</li>
              <li>
                Данные, связанные с оплатой: сведения о заказе и статусе транзакции (без хранения полных реквизитов банковских карт на стороне CardGenius, если
                обработка осуществляется сертифицированным платёжным провайдером).
              </li>
              <li>
                Служебные данные учётной записи: технические идентификаторы, настройки аккаунта, история операций с балансом geni в объёме, необходимом для
                учёта и отображения в интерфейсе.
              </li>
              <li>Рабочие данные: черновики карточек, тексты, фото, версии и иной контент, загружаемый Пользователем.</li>
              <li>
                Технические данные: IP-адрес, данные cookie и аналогичных технологий, сведения об устройстве и браузере, метаданные сессии, записи в служебных
                журналах (логах) при сбоях и инцидентах безопасности.
              </li>
              <li>При подключении WB API: данные, необходимые для автоматизации и синхронизации карточек в объёме, переданном API.</li>
            </ul>
            <h3>3. Цели и правовые основания обработки</h3>
            <ul>
              <li>Заключение и исполнение пользовательского соглашения (оказание услуг по доступу к платформе).</li>
              <li>
                Обработка персональных данных при оплате и учёте geni — исполнение договора и выполнение требований законодательства о бухгалтерском учёте и
                налоговой отчётности (в пределах, установленных законом).
              </li>
              <li>Направление сервисных и правовых уведомлений — исполнение договора и законные интересы Оператора по информированию пользователя.</li>
              <li>
                Обезличенный анализ для улучшения стабильности и качества сервиса — при наличии соответствующего правового основания и в объёме, не
                противоречащем законодательству Республики Казахстан.
              </li>
            </ul>
            <h3>4. Действия с персональными данными, сроки хранения</h3>
            <p>
              Оператор осуществляет сбор, запись, систематизацию, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передачу
              (предоставление, доступ), обезличивание, блокирование, удаление и уничтожение персональных данных — в объёме и способами, необходимыми для
              достижения целей обработки.
            </p>
            <p>
              Персональные данные хранятся не дольше, чем этого требуют цели обработки, если иной срок не установлен законом или договором. По достижении целей
              обработки или при отзыве согласия (где обработка основана на согласии) данные подлежат удалению или обезличиванию, если иное не предусмотрено
              законодательством Республики Казахстан.
            </p>
            <h3>5. Передача и поручение обработки</h3>
            <p>
              Оператор вправе передавать персональные данные третьим лицам, выступающим по поручению Оператора (хостинг, платёжные сервисы, службы доставки
              электронной почты, аналитика при наличии правового основания), при условии обеспечения конфиденциальности и безопасности в соответствии с
              законодательством Республики Казахстан о персональных данных. Трансграничная передача персональных данных осуществляется при наличии законных
              оснований, предусмотренных законодательством Республики Казахстан.
            </p>
            <p>Оператор не осуществляет распространение персональных данных в рекламных целях без отдельного согласия субъекта, если это требуется законом.</p>
            <h3>6. Меры по защите персональных данных</h3>
            <p>
              Оператор принимает необходимые организационные и технические меры для защиты персональных данных от неправомерного или случайного доступа,
              уничтожения, изменения, блокирования, копирования, распространения, а также от иных неправомерных действий, в объёме, соответствующем характеру
              обрабатываемых данных и рискам.
            </p>
            <h3>7. Права субъекта персональных данных</h3>
            <p>
              Субъект персональных данных вправе получать сведения об обработке его персональных данных, требовать уточнения, блокирования или уничтожения
              данных, если они являются неполными, устаревшими, неточными, незаконно полученными или не являются необходимыми для заявленной цели обработки, а
              также отозвать согласие на обработку персональных данных (если обработка основана на согласии) — в порядке, установленном законодательством
              Республики Казахстан.
            </p>
            <p>
              Обращения направляются Оператору по контактам, указанным в
              <button type="button" class="login-legal-inline-link" @click="openLegal('offer')">оферте</button>. Срок рассмотрения обращения — до 30 дней с
              момента получения, если иной срок не установлен законом.
            </p>
            <h3>8. Cookie и аналогичные технологии</h3>
            <p>
              Сервис может использовать файлы cookie и схожие технологии для обеспечения работы интерфейса, сохранения настроек и (при наличии соответствующего
              уведомления и согласия) аналитики. Пользователь может ограничить использование cookie в настройках браузера с учётом возможного снижения
              функциональности сервиса.
            </p>
            <h3>9. Изменение Политики</h3>
            <p>
              Оператор вправе актуализировать настоящую Политику. Новая редакция вступает в силу с момента её размещения на сайте, если иное не указано в тексте
              Политики. Рекомендуется периодически знакомиться с актуальной редакцией.
            </p>
          </template>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import {
  confirmPasswordReset,
  loginWithPhone,
  loginWithGoogleIdToken,
  resendPasswordResetOtp,
  resendPhoneOtp,
  startPhoneAuth,
  verifyPasswordResetOtp,
  verifyPhoneOtp,
} from "@/api/authApi";
import { AuthHttpError } from "@/api/geniBackend";
import { clearSocialCallbackUrl, parseSocialCallback, startSocialRedirectWithMode } from "@/api/socialOAuth";
import whatsappOriginalIcon from "@/assets/icons/New/Shape=Original, Colored=True.svg";
import brandLogoDark from "@/assets/branding/Full/Dark.svg";
import brandLogoLight from "@/assets/branding/Full/light.svg";
import versionsDarkIcon from "@/assets/content-icons/versions-dark.svg";
import versionsLightIcon from "@/assets/content-icons/versions-light.svg";
import wbLogo from "@/assets/marketplaces/wb.svg";
import CloudflareTurnstile from "@/components/auth/CloudflareTurnstile.vue";
import PasswordResetPhoneForm from "@/components/auth/PasswordResetPhoneForm.vue";
import Icon from "@/components/ui/Icon.vue";
import { useOtpCodeInput } from "@/composables/useOtpCodeInput";
import { createEmptyOtpDigits, formatPhoneInput, isPhoneComplete, joinOtpDigits, resolvePhoneInputValue } from "@/lib/phoneAuth";
import { isSocialAccountConflict, resolveSocialAuthErrorMessage } from "@/lib/socialAuthErrors";
import { captureEvent, trackRegistrationCompleted } from "@/lib/analytics";
import { trackGoogleAdsRegistrationConversion } from "@/lib/googleAds";
import {
  newFbCompleteRegistrationEventId,
  newFbLeadEventId,
  readFbPixelCookies,
  trackMetaPixelCompleteRegistration,
  trackMetaPixelLead,
} from "@/lib/metaPixel";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const emit = defineEmits<{ (e: "success", payload?: { fromRegistration?: boolean }): void }>();

const previousDocumentTitle = ref(typeof document !== "undefined" ? document.title : "");
const authMode = ref<"register" | "login">("register");
const authStep = ref<"phone" | "credentials" | "otp" | "resetPassword" | "resetPasswordNew">("phone");
/** Ветка сброса пароля на экране входа: телефон → OTP → новый пароль. */
const isPasswordResetOtpFlow = ref(false);
/** Счётчик неверных попыток OTP в reset-флоу — для трекинга «исчерпаны попытки» (бэк лимит 5). */
const passwordResetOtpFailedAttempts = ref(0);
const PASSWORD_RESET_OTP_MAX_ATTEMPTS = 5;
const phone = ref("");
const password = ref("");
const showPassword = ref(false);
const loading = ref(false);
const error = ref("");
const providerLoading = ref<null | "google">(null);
let socialCallbackInFlight = false;
const legalPage = ref<null | "offer" | "terms" | "privacy">(null);
const phoneTouched = ref(false);
const passwordTouched = ref(false);
const submitAttempted = ref(false);
const serverPhoneError = ref("");
const serverPasswordError = ref("");
const serverOtpError = ref("");
const challengeId = ref("");
const captchaToken = ref("");
const captchaInstanceKey = ref(0);
const resendRemainingSeconds = ref(0);
/** Blocks OTP verify while resend request is in flight (avoids stale challengeId races). */
const resendInFlight = ref(false);
const otpDigits = ref(createEmptyOtpDigits());
const recoveryPassword = ref("");
const recoveryPasswordConfirm = ref("");
const recoverySubmitAttempted = ref(false);
const showRecoveryPassword = ref(false);
const showRecoveryPasswordConfirm = ref(false);
const otpInputRefs = ref<Array<HTMLInputElement | null>>([]);
const {
  autofillInputRef,
  focusInput: focusOtpInput,
  clearAutofillInput,
  onAutofillInput: onOtpAutofillInput,
  onDigitInput: onOtpInput,
  onPaste: onOtpPaste,
  onKeydown: onOtpKeydown,
} = useOtpCodeInput({
  digits: otpDigits,
  inputRefs: otpInputRefs,
  onDigitsChange: () => {
    serverOtpError.value = "";
  },
  onComplete: () => {
    void submitOtpCode();
  },
});
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? "";
const captchaBypass = turnstileSiteKey.startsWith("1x") || turnstileSiteKey.startsWith("2x") || turnstileSiteKey.startsWith("3x");
let resendIntervalId: number | null = null;

const loginDocumentTitle = computed(() => {
  if (legalPage.value === "offer") return "CardGenius — Публичная оферта";
  if (legalPage.value === "terms") return "CardGenius — Условия использования";
  if (legalPage.value === "privacy") return "CardGenius — Конфиденциальность";
  if (authStep.value === "resetPassword" || authStep.value === "resetPasswordNew" || (authStep.value === "otp" && isPasswordResetOtpFlow.value)) {
    return "CardGenius — Сброс пароля";
  }
  return authMode.value === "register" ? "CardGenius — Регистрация" : "CardGenius — Вход";
});

const cardTitle = computed(() => {
  if (authStep.value === "resetPasswordNew") return "Новый пароль";
  if (authStep.value === "resetPassword" || (authStep.value === "otp" && isPasswordResetOtpFlow.value)) {
    return "Сброс пароля";
  }
  return authMode.value === "register" ? "Создайте профиль" : "Добро пожаловать";
});

const otpCardHint = computed(() => (isPasswordResetOtpFlow.value ? "" : "Введите 4 цифры из сообщения в WhatsApp."));

watch(
  loginDocumentTitle,
  (title) => {
    if (typeof document === "undefined") return;
    document.title = title;
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  socialCallbackInFlight = false;
  if (typeof document !== "undefined") {
    document.title = previousDocumentTitle.value || "CardGenius";
  }
  clearResendCountdown();
});

const clientPhoneError = computed(() => {
  if (!phoneTouched.value && !submitAttempted.value) return "";
  if (!phone.value.trim()) return "Введите номер WhatsApp.";
  if (!isPhoneComplete(phone.value)) return "Укажите корректный номер WhatsApp.";
  return "";
});

const clientPasswordError = computed(() => {
  if (!passwordTouched.value && !submitAttempted.value) return "";
  if (!password.value.trim()) return "Введите пароль.";
  if (password.value.length < 6) return "Пароль должен содержать минимум 6 символов.";
  return "";
});

const captchaError = computed(() => {
  if (authMode.value !== "register") return "";
  if (captchaBypass) return "";
  if (!submitAttempted.value || captchaToken.value) return "";
  if (!turnstileSiteKey) return "Настройте VITE_TURNSTILE_SITE_KEY для включения проверки.";
  return "Подтвердите, что вы не робот.";
});

const phoneError = computed(() => clientPhoneError.value || serverPhoneError.value);
const passwordError = computed(() => clientPasswordError.value || serverPasswordError.value);
const otpError = computed(() => serverOtpError.value);
const recoveryPasswordError = computed(() => {
  if (!recoverySubmitAttempted.value) return "";
  if (!recoveryPassword.value.trim()) return "Введите новый пароль.";
  if (recoveryPassword.value.length < 6) return "Пароль должен содержать минимум 6 символов.";
  return "";
});
const recoveryConfirmError = computed(() => {
  if (!recoverySubmitAttempted.value) return "";
  if (recoveryPasswordConfirm.value !== recoveryPassword.value) return "Пароли не совпадают.";
  return "";
});
const isAnyLoading = computed(() => loading.value || Boolean(providerLoading.value));
const canContinueWithPhone = computed(() => !isAnyLoading.value && isPhoneComplete(phone.value));
const canSubmitCredentials = computed(() => {
  return (
    !isAnyLoading.value &&
    !clientPhoneError.value &&
    !clientPasswordError.value &&
    (authMode.value === "login" || captchaBypass || Boolean(captchaToken.value)) &&
    isPhoneComplete(phone.value) &&
    password.value.length >= 6
  );
});
const credentialsButtonLabel = computed(() => {
  return authMode.value === "register" ? "Получить код" : "Войти";
});
const formattedPhone = computed(() => formatPhoneInput(phone.value) || phone.value);
const legalTitle = computed(() => {
  if (legalPage.value === "offer") return "Публичная оферта";
  if (legalPage.value === "terms") return "Условия использования";
  return "Конфиденциальность";
});
const legalSubtitle = computed(() => {
  if (legalPage.value === "offer") return "Оферта на заключение пользовательского соглашения для использования платформы CardGenius.";
  if (legalPage.value === "terms") return "Правила доступа и работы с платформой CardGenius для подготовки карточек товаров Wildberries.";
  return "Как CardGenius обрабатывает и защищает данные пользователей сервиса.";
});

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function clearRecoveryDraft() {
  recoveryPassword.value = "";
  recoveryPasswordConfirm.value = "";
  recoverySubmitAttempted.value = false;
  showRecoveryPassword.value = false;
  showRecoveryPasswordConfirm.value = false;
}

function resetCaptcha() {
  captchaToken.value = "";
  captchaInstanceKey.value += 1;
}

function clearResendCountdown() {
  if (resendIntervalId !== null) {
    window.clearInterval(resendIntervalId);
    resendIntervalId = null;
  }
}

function startResendCountdown(seconds: number) {
  clearResendCountdown();
  resendRemainingSeconds.value = seconds;

  if (seconds <= 0) {
    return;
  }

  resendIntervalId = window.setInterval(() => {
    if (resendRemainingSeconds.value <= 1) {
      resendRemainingSeconds.value = 0;
      clearResendCountdown();
      return;
    }

    resendRemainingSeconds.value -= 1;
  }, 1000);
}

function resetOtpState() {
  otpDigits.value = createEmptyOtpDigits();
  clearAutofillInput();
  challengeId.value = "";
  startResendCountdown(0);
}

function setOtpInputRef(element: Element | null, index: number) {
  otpInputRefs.value[index] = element instanceof HTMLInputElement ? element : null;
}

function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement;
  phone.value = resolvePhoneInputValue(target.value, event);
  serverPhoneError.value = "";
}

function onPasswordInput() {
  serverPasswordError.value = "";
}

function validatePhoneStep(): boolean {
  phoneTouched.value = true;
  submitAttempted.value = true;
  return !clientPhoneError.value;
}

function validateCredentialsStep(): boolean {
  phoneTouched.value = true;
  passwordTouched.value = true;
  submitAttempted.value = true;
  return !clientPhoneError.value && !clientPasswordError.value && (authMode.value === "login" || captchaBypass || Boolean(captchaToken.value));
}

function continueWithPhone() {
  if (!validatePhoneStep()) {
    return;
  }

  captureEvent("[CardGenius] Продолжить после ввода телефона", {
    phone: formatPhoneInput(phone.value) || phone.value || null,
  });

  authStep.value = "credentials";
  // resetServerErrors();
  // GC-18: НЕ сбрасываем капчу при переходе phone → credentials.
  // Если токен уже валиден (юзер вернулся назад и идёт снова), Cloudflare token держит ~5 мин —
  // пусть переиспользуется. При реальной серверной ошибке catch внизу всё равно сбросит.
}

function openPasswordReset() {
  authStep.value = "resetPassword";
  isPasswordResetOtpFlow.value = false;
  passwordResetOtpFailedAttempts.value = 0;
  error.value = "";
  // resetServerErrors();
  clearRecoveryDraft();
  startResendCountdown(0);
  captureEvent("[CardGenius] Открыт сброс пароля", { surface: "login_screen" });
}

function leavePasswordReset() {
  isPasswordResetOtpFlow.value = false;
  authStep.value = "credentials";
  error.value = "";
  // resetServerErrors();
  resetOtpState();
  clearRecoveryDraft();
  startResendCountdown(0);
}

function onPasswordResetCodeSent(payload: { challengeId: string; resendAvailableInSeconds: number }) {
  challengeId.value = payload.challengeId;
  isPasswordResetOtpFlow.value = true;
  authStep.value = "otp";
  otpDigits.value = createEmptyOtpDigits();
  serverOtpError.value = "";
  error.value = "";
  startResendCountdown(payload.resendAvailableInSeconds);
  focusOtpInput(0);
}

async function submitCredentials() {
  if (!validateCredentialsStep()) {
    return;
  }

  if (authMode.value === "register") {
    captureEvent("[CardGenius] Получить код", {
      phone: formatPhoneInput(phone.value) || phone.value || null,
    });
  } else {
    captureEvent("[CardGenius] Вход по паролю", {
      phone: formatPhoneInput(phone.value) || phone.value || null,
    });
  }

  loading.value = true;
  error.value = "";
  // resetServerErrors();

  try {
    if (authMode.value === "login") {
      await loginWithPhone(phone.value, password.value);
      emit("success", { fromRegistration: false });
      return;
    }

    const fbLead =
      authMode.value === "register" && typeof window !== "undefined"
        ? (() => {
            const fbLeadEventId = newFbLeadEventId();
            trackMetaPixelLead(fbLeadEventId);
            return {
              fbLeadEventId,
              fbEventSourceUrl: window.location.href,
              ...readFbPixelCookies(),
            };
          })()
        : undefined;

    const response = await startPhoneAuth(
      authMode.value,
      phone.value,
      password.value,
      authMode.value === "register" ? captchaToken.value : undefined,
      fbLead,
    );
    challengeId.value = response.challengeId;
    authStep.value = "otp";
    otpDigits.value = createEmptyOtpDigits();
    startResendCountdown(response.resendAvailableInSeconds);
    focusOtpInput(0);
  } catch (reason) {
    // GC-19: 409 при register = «номер уже занят». Показываем ошибку под полем телефона.
    if (
      authMode.value === "register" &&
      reason instanceof AuthHttpError &&
      reason.status === 409
    ) {
      captureEvent("[CardGenius] Регистрация: номер уже занят", {
        phone: formatPhoneInput(phone.value) || phone.value || null,
      });
      serverPhoneError.value = reason.apiMessage?.trim() || "Этот номер уже зарегистрирован";
      resetCaptcha();
      loading.value = false;
      return;
    }

    const message = reason instanceof Error ? reason.message : authMode.value === "register" ? "Не удалось отправить код" : "Не удалось выполнить вход";
    const lower = message.toLowerCase();

    if (lower.includes("телефон")) {
      serverPhoneError.value = message;
    } else if (lower.includes("парол")) {
      serverPasswordError.value = message;
    } else {
      error.value = message;
    }

    if (authMode.value === "register") {
      resetCaptcha();
    }
  } finally {
    loading.value = false;
  }
}

async function submitOtpCode() {
  const otp = joinOtpDigits(otpDigits.value);
  if (loading.value || resendInFlight.value || otp.length !== 4) {
    return;
  }

  if (isPasswordResetOtpFlow.value) {
    if (!challengeId.value) return;
    loading.value = true;
    error.value = "";
    serverOtpError.value = "";
    try {
      await verifyPasswordResetOtp(challengeId.value, otp);
      captureEvent("[CardGenius] Результат проверки кода", {
        is_correct: true,
        flow: "password_reset",
      });
      passwordResetOtpFailedAttempts.value = 0;
      authStep.value = "resetPasswordNew";
      recoverySubmitAttempted.value = false;
    } catch (reason) {
      captureEvent("[CardGenius] Результат проверки кода", {
        is_correct: false,
        flow: "password_reset",
      });
      passwordResetOtpFailedAttempts.value += 1;
      if (passwordResetOtpFailedAttempts.value >= PASSWORD_RESET_OTP_MAX_ATTEMPTS) {
        captureEvent("[CardGenius] Сброс пароля: исчерпаны попытки OTP", {
          surface: "login_screen",
        });
      }
      serverOtpError.value = reason instanceof Error ? reason.message : "Не удалось подтвердить код";
      otpDigits.value = createEmptyOtpDigits();
      clearAutofillInput();
      focusOtpInput(0);
    } finally {
      loading.value = false;
    }
    return;
  }

  if (!challengeId.value) {
    return;
  }

  loading.value = true;
  error.value = "";
  serverOtpError.value = "";

  try {
    const fbComplete =
      authMode.value === "register" && typeof window !== "undefined"
        ? {
            fbCompleteRegistrationEventId: newFbCompleteRegistrationEventId(),
            fbEventSourceUrl: window.location.href,
            ...readFbPixelCookies(),
          }
        : undefined;

    await verifyPhoneOtp(challengeId.value, otp, fbComplete);
    captureEvent("[CardGenius] Результат проверки кода", {
      is_correct: true,
      flow: "register_phone",
    });
    if (authMode.value === "register") {
      trackRegistrationCompleted({ channel: "phone", intent: "register" });
      if (fbComplete?.fbCompleteRegistrationEventId) {
        trackMetaPixelCompleteRegistration(fbComplete.fbCompleteRegistrationEventId);
        trackGoogleAdsRegistrationConversion();
      }
    }
    isPasswordResetOtpFlow.value = false;
    emit("success", { fromRegistration: authMode.value === "register" });
  } catch (reason) {
    captureEvent("[CardGenius] Результат проверки кода", {
      is_correct: false,
      flow: "register_phone",
    });
    const message = reason instanceof Error ? reason.message : "Не удалось подтвердить код";
    serverOtpError.value = message;
    otpDigits.value = createEmptyOtpDigits();
    clearAutofillInput();
    focusOtpInput(0);
  } finally {
    loading.value = false;
  }
}

async function resendOtpCode() {
  if (resendRemainingSeconds.value > 0 || loading.value || resendInFlight.value) {
    return;
  }

  captureEvent("[CardGenius] Отправить код повторно", {
    is_password_reset: isPasswordResetOtpFlow.value,
  });

  if (isPasswordResetOtpFlow.value) {
    if (!challengeId.value) return;
    loading.value = true;
    resendInFlight.value = true;
    serverOtpError.value = "";
    error.value = "";
    try {
      const response = await resendPasswordResetOtp(challengeId.value);
      challengeId.value = response.challengeId;
      passwordResetOtpFailedAttempts.value = 0;
      otpDigits.value = createEmptyOtpDigits();
      startResendCountdown(response.resendAvailableInSeconds);
      focusOtpInput(0);
    } catch (reason) {
      error.value = reason instanceof Error ? reason.message : "Не удалось переотправить код";
    } finally {
      resendInFlight.value = false;
      loading.value = false;
    }
    return;
  }

  if (!challengeId.value) {
    return;
  }

  loading.value = true;
  resendInFlight.value = true;
  serverOtpError.value = "";
  error.value = "";

  try {
    const response = await resendPhoneOtp(challengeId.value);
    challengeId.value = response.challengeId;
    otpDigits.value = createEmptyOtpDigits();
    startResendCountdown(response.resendAvailableInSeconds);
    focusOtpInput(0);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "Не удалось переотправить код";
  } finally {
    resendInFlight.value = false;
    loading.value = false;
  }
}

async function submitRecoveryPassword() {
  recoverySubmitAttempted.value = true;
  if (recoveryPasswordError.value || recoveryConfirmError.value) {
    return;
  }
  if (!challengeId.value) {
    error.value = "Сессия восстановления истекла. Начните заново.";
    return;
  }

  const otp = joinOtpDigits(otpDigits.value);

  loading.value = true;
  error.value = "";
  try {
    await confirmPasswordReset(challengeId.value, otp, recoveryPassword.value);
    captureEvent("[CardGenius] Сброс пароля: завершён", { surface: "login_screen" });
    isPasswordResetOtpFlow.value = false;
    passwordResetOtpFailedAttempts.value = 0;
    authStep.value = "credentials";
    resetOtpState();
    clearRecoveryDraft();
    startResendCountdown(0);
    password.value = "";
    emit("success", { fromRegistration: false });
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "Не удалось обновить пароль";
  } finally {
    loading.value = false;
  }
}

function setAuthMode(mode: "register" | "login") {
  if (mode === "register") {
    captureEvent("[CardGenius] Переход к регистрации");
  } else {
    captureEvent("[CardGenius] Переход ко входу");
  }
  authMode.value = mode;
  authStep.value = mode === "register" ? "phone" : "credentials";
  isPasswordResetOtpFlow.value = false;
  error.value = "";
  // Clear the password when switching between login/register so it doesn't carry over.
  password.value = "";
  passwordTouched.value = false;
  // resetServerErrors();
  resetOtpState();
  resetCaptcha();
  clearRecoveryDraft();
}

async function loginWithProvider(provider: "google") {
  providerLoading.value = provider;
  error.value = "";
  captureEvent("[CardGenius] Вход через соцсеть", {
    provider,
    intent: authMode.value,
  });
  // resetServerErrors();
  try {
    startSocialRedirectWithMode(provider, authMode.value);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "Не удалось запустить вход через провайдера.";
  } finally {
    providerLoading.value = null;
  }
}

function applyExistingAccountPhoneLoginUi() {
  authMode.value = "login";
  authStep.value = "credentials";
  serverPhoneError.value = "";
  serverPasswordError.value = "";
}

async function processSocialCallback() {
  const callback = parseSocialCallback();
  if (!callback) return;
  if (socialCallbackInFlight) return;
  socialCallbackInFlight = true;

  try {
  if ("error" in callback) {
    error.value = callback.error;
    clearSocialCallbackUrl();
    return;
  }

  if (callback.mode === "link") {
    return;
  }

  authMode.value = callback.mode;
  authStep.value = callback.mode === "register" ? "phone" : "credentials";
  providerLoading.value = callback.provider;
  error.value = "";
  // resetServerErrors();
  try {
    const fbComplete =
      callback.mode === "register" && typeof window !== "undefined"
        ? {
            fbCompleteRegistrationEventId: newFbCompleteRegistrationEventId(),
            fbEventSourceUrl: window.location.href,
            ...readFbPixelCookies(),
          }
        : undefined;

    const { isNewUser } = await loginWithGoogleIdToken(callback.idToken, fbComplete);
    clearSocialCallbackUrl();
    if (isNewUser) {
      trackRegistrationCompleted({ channel: callback.provider, intent: callback.mode });
    }
    if (callback.mode === "register" && isNewUser && fbComplete?.fbCompleteRegistrationEventId) {
      trackMetaPixelCompleteRegistration(fbComplete.fbCompleteRegistrationEventId);
      trackGoogleAdsRegistrationConversion();
    }
    emit("success", { fromRegistration: callback.mode === "register" && isNewUser });
  } catch (reason) {
    clearSocialCallbackUrl();
    const fallback =
      callback.mode === "register"
        ? "Не удалось завершить регистрацию через провайдера."
        : "Не удалось завершить вход через провайдера.";

    if (isSocialAccountConflict(reason)) {
      applyExistingAccountPhoneLoginUi();
      captureEvent("[CardGenius] Google вход: аккаунт уже существует", {
        intent: callback.mode,
        http_status: 409,
      });
    }

    error.value = resolveSocialAuthErrorMessage(reason, fallback);
  } finally {
    providerLoading.value = null;
  }
  } finally {
    socialCallbackInFlight = false;
  }
}

function goBack() {
  if (legalPage.value) {
    legalPage.value = null;
    return;
  }

  if (authStep.value === "resetPasswordNew") {
    authStep.value = "otp";
    otpDigits.value = createEmptyOtpDigits();
    serverOtpError.value = "";
    recoverySubmitAttempted.value = false;
    focusOtpInput(0);
    return;
  }

  if (authStep.value === "otp") {
    if (isPasswordResetOtpFlow.value) {
      isPasswordResetOtpFlow.value = false;
      authStep.value = "resetPassword";
      resetOtpState();
      return;
    }
    authStep.value = "credentials";
    otpDigits.value = createEmptyOtpDigits();
    serverOtpError.value = "";
    startResendCountdown(0);
    return;
  }

  if (authStep.value === "resetPassword") {
    authStep.value = "credentials";
    startResendCountdown(0);
    return;
  }

  if (authStep.value === "credentials") {
    authStep.value = "phone";
    // GC-18: НЕ сбрасываем капчу при возврате credentials → phone.
    // На phone-экране капчи нет — токен просто остаётся; при возврате на credentials
    // виджет переиспользуется и юзер не проходит капчу второй раз.
    // resetServerErrors();
    return;
  }

  if (typeof window === "undefined") return;
  if (window.history.length > 1) {
    window.history.back();
    return;
  }
  window.location.reload();
}

function openLegal(kind: "offer" | "terms" | "privacy") {
  legalPage.value = kind;
}

onMounted(() => {
  if (authMode.value === "login") {
    authStep.value = "credentials";
  }
  processSocialCallback().catch((reason) => {
    console.error("[LoginScreen] processSocialCallback failed", reason);
  });
});
</script>

<style scoped lang="scss">
.login-screen {
  min-height: 100dvh;
  background: var(--sidebar-border);
  color: var(--foreground);
  padding: 0 clamp(16px, 3vw, 32px);
}

.login-header {
  position: sticky;
  top: 0;
  z-index: 5;
  background: color-mix(in oklch, var(--sidebar-border) 92%, transparent);
  border-bottom: 1px solid color-mix(in oklch, var(--border) 65%, transparent);
  backdrop-filter: blur(8px);
}

.login-header__inner {
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.login-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.login-header__back {
  height: 32px;
  min-height: 32px;
  border-radius: 10px;
  border: 1px solid color-mix(in oklch, var(--border) 88%, transparent);
  background: var(--secondary);
  color: var(--foreground);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background-color 160ms ease,
    color 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;
}

.login-header__back:hover {
  background: color-mix(in oklch, var(--muted) 84%, var(--secondary));
  color: var(--foreground);
  border-color: color-mix(in oklch, var(--ring) 30%, var(--border));
}

.login-header__back:active {
  transform: translateY(1px);
}

.login-header__back:focus-visible {
  outline: none;
  border-color: color-mix(in oklch, var(--ring) 36%, var(--border));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 22%, transparent);
}

.login-header__back-icon {
  color: var(--muted-foreground);
}

.login-header__back-label {
  display: inline-flex;
  align-items: center;
}

.login-header__brand {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.login-main {
  padding: 24px 0 40px;
}

.login-content {
  display: grid;
  gap: 20px;
}

.login-legal-head {
  display: grid;
  gap: 6px;
}

.login-legal-head__subtitle {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--muted-foreground);
}

.login-intro {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login-intro__subtitle {
  margin: 0;
}

.login-intro__market-badge {
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  gap: 6px;
  height: 24px;
  border-radius: 8px;
  padding: 0 10px;
  margin: 0 6px;
  border: 0;
  background: var(--color-brand-wb-bg);
  color: var(--color-brand-wb);
  font-size: 12px;
  font-weight: 500;
}

.login-intro__chips {
  margin: 0;
  padding: 0;
  list-style: none;
}

.login-chip {
  white-space: nowrap;
}

.login-chip__icon {
  position: relative;
}

.login-chip__icon--electric {
  display: inline-flex;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
}

.login-chip__electric-bolt {
  position: relative;
  z-index: 2;
  color: var(--color-brand-primary);
}

.login-chip__electric-halo {
  position: absolute;
  inset: 1px;
  border-radius: 999px;
  background: radial-gradient(circle, color-mix(in oklab, var(--color-brand-primary) 42%, white) 0%, transparent 68%);
  opacity: 0.2;
  transform: translateY(1px) scale(0.9);
  animation: login-chip-electric-halo 1.3s ease-in-out infinite;
}

.login-chip__electric-spark {
  position: absolute;
  width: 7px;
  height: 10px;
  clip-path: polygon(38% 0%, 82% 0%, 57% 35%, 100% 35%, 30% 100%, 46% 58%, 8% 58%);
  box-shadow: 0 0 10px var(--cg-spark-color, #ffcc00);
  opacity: 0;
}

.login-chip__electric-spark-a {
  --cg-spark-color: #ffcc00;
  top: 1px;
  right: -2px;
  transform-origin: 40% 55%;
  background: var(--cg-spark-color);
  animation: login-chip-electric-spark-a 1.05s steps(5, end) infinite;
}

.login-chip__electric-spark-b {
  --cg-spark-color: #ff453a;
  bottom: 1px;
  left: -2px;
  transform-origin: 52% 48%;
  background: var(--cg-spark-color);
  animation: login-chip-electric-spark-b 1.05s steps(5, end) infinite;
}

.login-chip__electric-spark-c {
  --cg-spark-color: #ff453a;
  top: -1px;
  left: 2px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
  animation: login-chip-electric-spark-c 1.05s steps(5, end) infinite;
}

.login-chip__electric-spark-d {
  --cg-spark-color: #ffcc00;
  bottom: -1px;
  right: 2px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
  animation: login-chip-electric-spark-d 1.05s steps(5, end) infinite;
}

.login-chip__icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.login-card {
  border-radius: calc(var(--radius) + 0.25rem);
  border: 1px solid color-mix(in oklch, var(--border) 75%, transparent);
  background: var(--card);
  padding: clamp(16px, 2.4vw, 24px);
  padding-bottom: 36px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  text-align: center;
}

.login-legal-card {
  border-radius: calc(var(--radius) + 0.25rem);
  border: 1px solid color-mix(in oklch, var(--border) 75%, transparent);
  background: var(--card);
  padding: clamp(16px, 2.4vw, 24px);
  display: grid;
  gap: 12px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--muted-foreground);
}

.login-legal-card h3 {
  margin: 2px 0 0;
  font-size: 16px;
  line-height: 1.3;
  font-weight: 600;
  color: var(--foreground);
}

.login-legal-card p {
  margin: 0;
}

.login-legal-card ul {
  margin: 0;
  padding-left: 18px;
  display: grid;
  gap: 6px;
}

.login-card__header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  column-gap: 12px;
  justify-items: center;
  text-align: center;
}

.login-card__title {
  margin: 0;
  font-size: clamp(24px, 3.2vw, 34px);
  font-weight: 700;
  line-height: 1.2;
  text-wrap: balance;
}

.login-card__subtitle {
  margin: 0;
  font-size: 15px;
  line-height: 1.35;
  color: var(--muted-foreground);
}

.login-card__subtitle-link {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0 0 0 4px;
  color: var(--foreground);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.login-fields {
  display: grid;
  gap: 10px;
}

.login-field {
  display: grid;
  gap: 6px;
  text-align: left;
}

.login-field span {
  font-size: 13px;
  font-weight: 500;
}

.login-field__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.login-field__label-row span {
  min-width: 0;
}

/* Подпись к полю пароля: отдельный <label for> — нельзя оборачивать кнопки в общий <label> (первая кнопка
   становится «целью» label, из‑за этого клик по show/hole пароля открывал «Забыли пароль?»). */
.login-field__password-heading {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
}

.login-forgot-password {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  flex-shrink: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--foreground);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.login-forgot-password:hover {
  color: var(--muted-foreground);
}

.login-forgot-password:focus-visible {
  outline: none;
  border-radius: 6px;
  box-shadow: 0 0 0 2px color-mix(in oklch, var(--ring) 22%, transparent);
}

.login-field input {
  height: 40px;
  width: 100%;
  border-radius: 10px;
  border: 1px solid color-mix(in oklch, var(--border) 82%, transparent);
  background: var(--background);
  color: var(--foreground);
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease;
}

.login-field__input-wrap {
  position: relative;
}

.login-field__input-wrap input {
  padding-right: 42px;
}

.login-field__input-wrap--phone input {
  padding-left: 40px;
  padding-right: 12px;
}

.login-field__leading-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  width: 20px;
  height: 20px;
  transform: translateY(-50%);
  pointer-events: none;
  flex-shrink: 0;
}

.login-field__password-toggle {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--muted-foreground);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.login-field__password-toggle:hover {
  color: var(--foreground);
}

.login-field__password-toggle:focus-visible {
  outline: none;
  border-radius: 6px;
  box-shadow: 0 0 0 2px color-mix(in oklch, var(--ring) 22%, transparent);
}

.login-field input::placeholder {
  color: var(--muted-foreground);
}

.login-field input:focus-visible {
  border-color: color-mix(in oklch, var(--ring) 42%, var(--border));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 20%, transparent);
}

.login-field input.is-invalid {
  border-color: color-mix(in oklch, var(--destructive) 70%, var(--border));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--destructive) 16%, transparent);
}

.login-field__hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.3;
}

.login-field__hint--error {
  color: var(--destructive);
}

.login-email-start {
  display: grid;
  gap: 12px;
  width: min(100%, 420px);
}

.login-register-stack {
  width: min(100%, 420px);
  margin-inline: auto;
  display: grid;
  gap: 14px;
}

.login-email-start .login-field {
  width: 100%;
  max-width: 100%;
}

.login-step-note {
  display: grid;
  gap: 8px;
  text-align: left;
}

.login-step-note__title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
}

.login-email-start__continue {
  width: 100%;
  min-height: 46px;
  border: 0;
  border-radius: 10px;
  background: #000;
  color: #fff;
  font-size: 14px;
  line-height: 1;
  font-weight: 600;
  cursor: pointer;
  transition:
    opacity 120ms ease,
    transform 120ms ease;
}

.login-email-start__continue-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.login-email-start__electric {
  position: relative;
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  margin-right: -2px;
}

.login-email-start__electric-bolt {
  position: relative;
  z-index: 2;
  color: var(--color-brand-primary);
}

.login-email-start__electric-halo {
  position: absolute;
  inset: 1px;
  border-radius: 999px;
  background: radial-gradient(circle, color-mix(in oklab, var(--color-brand-primary) 42%, white) 0%, transparent 68%);
  opacity: 0.2;
  transform: translateY(1px) scale(0.9);
}

.login-email-start__electric-spark {
  position: absolute;
  width: 8px;
  height: 11px;
  clip-path: polygon(38% 0%, 82% 0%, 57% 35%, 100% 35%, 30% 100%, 46% 58%, 8% 58%);
  box-shadow: 0 0 10px var(--cg-spark-color, #ffcc00);
  opacity: 0;
}

.login-email-start__electric-spark-a {
  --cg-spark-color: #ffcc00;
  top: 1px;
  right: -1px;
  transform-origin: 40% 55%;
  background: var(--cg-spark-color);
}

.login-email-start__electric-spark-b {
  --cg-spark-color: #ff453a;
  bottom: 1px;
  left: -2px;
  transform-origin: 52% 48%;
  background: var(--cg-spark-color);
}

.login-email-start__electric-spark-c {
  --cg-spark-color: #ff453a;
  top: -1px;
  left: 3px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
}

.login-email-start__electric-spark-d {
  --cg-spark-color: #ffcc00;
  bottom: -1px;
  right: 2px;
  transform-origin: 50% 50%;
  background: var(--cg-spark-color);
}

.login-email-start__continue:hover:not(:disabled) {
  opacity: 0.95;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-halo {
  animation: login-chip-electric-halo 1.3s ease-in-out infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-a {
  animation: login-chip-electric-spark-a 1.05s steps(5, end) infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-b {
  animation: login-chip-electric-spark-b 1.05s steps(5, end) infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-c {
  animation: login-chip-electric-spark-c 1.05s steps(5, end) infinite;
}

.login-email-start__continue:hover:not(:disabled) .login-email-start__electric-spark-d {
  animation: login-chip-electric-spark-d 1.05s steps(5, end) infinite;
}

.login-email-start__continue:active:not(:disabled) {
  transform: translateY(1px);
}

.login-email-start__continue:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.login-social-block {
  display: grid;
  gap: 10px;
  padding-top: 24px;
}

.login-social-block__label {
  margin: 0;
  font-size: 15px;
  line-height: 1.35;
  color: var(--muted-foreground);
}

.login-otp-card {
  display: grid;
  gap: 14px;
  width: min(100%, 420px);
  margin-inline: auto;
}

.login-otp-card__subtitle,
.login-otp-card__hint,
.login-otp-card__resend {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--muted-foreground);
}

.login-otp-card__phone {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--foreground);
}

.login-otp-card__error {
  text-align: center;
}

.login-otp-card__loading {
  margin-top: 4px;
}

.login-otp-card__resend-link {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  color: var(--foreground);
  text-decoration: underline;
  text-underline-offset: 3px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.login-otp-card__resend-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-otp-inputs {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.login-otp-autofill-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0.001;
  background: transparent;
  color: transparent;
  caret-color: transparent;
  pointer-events: none;
  z-index: 1;
}

.login-otp-input {
  position: relative;
  z-index: 2;
  height: 56px;
  border-radius: 14px;
  border: 1px solid color-mix(in oklch, var(--border) 82%, transparent);
  background: var(--background);
  color: var(--foreground);
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  outline: none;
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease;
}

.login-otp-input:focus-visible {
  border-color: color-mix(in oklch, var(--ring) 42%, var(--border));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 20%, transparent);
}

.login-social-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.login-social-actions__button {
  flex: 1 1 0;
  min-height: 44px;
  border-radius: 999px;
  border: 1px solid color-mix(in oklch, var(--border) 82%, transparent);
  background: #fff;
  color: var(--foreground);
  padding: 0 10px;
  padding-bottom: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.1;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    opacity 140ms ease,
    transform 120ms ease;
}

.login-social-actions__button:hover:not(:disabled) {
  background: color-mix(in oklch, var(--muted) 64%, var(--card));
  border-color: color-mix(in oklch, var(--ring) 26%, var(--border));
}

.login-social-actions__button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--ring) 24%, transparent);
}

.login-social-actions__button:active:not(:disabled) {
  transform: translateY(1px);
}

.login-social-actions__button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-social-actions__button--icon {
  min-width: 96px;
}

.login-social-actions__brand {
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.login-social-actions__brand svg {
  width: 100%;
  height: 100%;
  display: block;
}

.login-social-actions__button--google {
  background: #ffffff;
  border-color: #dadce0;
  color: #1f1f1f;
}

.login-social-actions__button--google:hover:not(:disabled) {
  background: #4285f4;
  border-color: #4285f4;
  color: #fff;
}

.login-social-actions__button--google:active:not(:disabled) {
  background: #3b78dc;
  border-color: #3b78dc;
  color: #fff;
}

.login-social-actions__button--google:hover:not(:disabled) .login-social-actions__brand--google svg path,
.login-social-actions__button--google:active:not(:disabled) .login-social-actions__brand--google svg path {
  fill: #fff !important;
}

.login-social-actions__button--google:focus-visible {
  box-shadow: 0 0 0 3px color-mix(in oklch, #4285f4 28%, transparent);
}

.login-social-actions__brand--google {
  width: 20px;
  height: 20px;
}

.login-social-actions__button--apple {
  background: #ffffff;
  border-color: #dadce0;
  color: #000000;
}

.login-social-actions__button--apple:hover:not(:disabled) {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}

.login-social-actions__button--apple:active:not(:disabled) {
  background: #111111;
  border-color: #111111;
  color: #ffffff;
}

.login-social-actions__button--apple:focus-visible {
  box-shadow: 0 0 0 3px color-mix(in oklch, #000000 30%, transparent);
}

.login-social-actions__brand--apple {
  width: 24px;
  height: 24px;
}

.login-social-actions__brand--apple svg {
  width: 25px;
  height: 25px;
  margin: auto;
  transform: translateY(0.5px);
  transform-origin: center;
}

.actions {
  display: flex;
  gap: 8px;
}

.actions button {
  flex: 1 1 0;
  height: 38px;
  border: 0;
  border-radius: 10px;
  padding: 0 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  transition:
    opacity 140ms ease,
    transform 120ms ease,
    background-color 160ms ease;
}

.actions button:active:not(:disabled) {
  transform: translateY(1px);
}

.actions button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.actions button:first-child {
  background: #000;
  color: #fff;
}

.actions .ghost {
  background: color-mix(in oklch, var(--foreground) 8%, transparent);
  color: var(--foreground);
}

.actions .ghost:hover:not(:disabled) {
  background: color-mix(in oklch, var(--foreground) 12%, transparent);
}

.login-legal-consent {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--muted-foreground);
}

.login-legal-consent__link,
.login-legal-inline-link {
  appearance: none;
  border: 0;
  padding: 0;
  margin: 0 2px;
  background: transparent;
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

.login-legal-consent__link:hover,
.login-legal-inline-link:hover {
  color: var(--foreground);
}

.error {
  color: var(--color-error);
  margin: 0;
  font-size: 13px;
}

.is-spin {
  animation: login-spin 900ms linear infinite;
}

@keyframes login-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes login-chip-electric-halo {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.86);
  }
  40% {
    opacity: 0.7;
    transform: scale(1.08);
  }
  65% {
    opacity: 0.45;
    transform: scale(0.95);
  }
}

@keyframes login-chip-electric-spark-a {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(8deg) scale(0.6);
  }
  8% {
    opacity: 1;
    transform: translate(1px, -1px) rotate(16deg) scale(1);
  }
  14% {
    opacity: 0;
    transform: translate(4px, -3px) rotate(22deg) scale(0.8);
  }
  24% {
    opacity: 0.95;
    transform: translate(2px, -2px) rotate(14deg) scale(0.96);
  }
  31% {
    opacity: 0;
    transform: translate(6px, -4px) rotate(23deg) scale(0.7);
  }
}

@keyframes login-chip-electric-spark-b {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  42% {
    opacity: 0;
    transform: translate(0, 0) rotate(-12deg) scale(0.6);
  }
  52% {
    opacity: 1;
    transform: translate(-1px, 1px) rotate(-22deg) scale(1);
  }
  58% {
    opacity: 0;
    transform: translate(-4px, 3px) rotate(-26deg) scale(0.8);
  }
  68% {
    opacity: 0.9;
    transform: translate(-2px, 2px) rotate(-20deg) scale(0.94);
  }
  75% {
    opacity: 0;
    transform: translate(-6px, 5px) rotate(-28deg) scale(0.72);
  }
}

@keyframes login-chip-electric-spark-c {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  30% {
    opacity: 0;
    transform: translate(0, 0) rotate(2deg) scale(0.58);
  }
  36% {
    opacity: 0.95;
    transform: translate(1px, -2px) rotate(10deg) scale(0.98);
  }
  43% {
    opacity: 0;
    transform: translate(3px, -5px) rotate(16deg) scale(0.72);
  }
}

@keyframes login-chip-electric-spark-d {
  0%,
  100% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  60% {
    opacity: 0;
    transform: translate(0, 0) rotate(-4deg) scale(0.58);
  }
  67% {
    opacity: 0.95;
    transform: translate(1px, 2px) rotate(-11deg) scale(0.98);
  }
  74% {
    opacity: 0;
    transform: translate(3px, 5px) rotate(-18deg) scale(0.72);
  }
}

@media (max-width: 640px) {
  .login-header__back {
    font-size: 12px;
  }

  .login-header__inner {
    min-height: 64px;
  }

  .login-header__brand {
    height: 26px;
  }

  .login-main {
    padding-top: 20px;
  }

  .login-content {
    gap: 16px;
  }

  .actions {
    flex-direction: column;
    align-items: stretch;
  }

  .actions button {
    flex: 0 0 auto;
    width: 100%;
    height: 40px;
    min-height: 40px;
    white-space: nowrap;
  }
}

@media (min-width: 768px) {
  .login-header__back {
    font-size: 14px;
  }

  .login-main {
    padding-bottom: 64px;
  }
}
</style>
